<template>
  <component
    :is="product.Slug ? 'a' : 'div'"
    class="product-tile-fi"
    :href="productHref"
    @click.stop.prevent="goToProduct(product.id)"
  >
    <div class="product-tile-fi__image">
      <cloud-image-fi
        v-if="product.image"
        :src="product.image"
        :alt="product.name"
        :aspect-ratio="1"
        trim
        params="func=fit"
        class="product-tile-fi__img"
        do-not-replace-url
      />
    </div>

    <div class="product-tile-fi__artnr">
      {{ $dict.get('construction-Projects.Global-Labels.Article-Number', [product.id]) }}
    </div>
    <div class="product-tile-fi__name">
      {{ product.name }}
    </div>

    <div class="product-tile-fi__action">
      <template v-if="!isFite">
        <template v-if="isShop">
          <div class="product-tile-fi__product-facts">
            <loading-indicator-wrapper
              class="product-tile-fi__loading-indicator"
              :loading="bestPriceLoading || loading"
              @click.stop.prevent
            >
              <div
                :class="{
                  'product-tile-fi__details': true,
                  'product-tile-fi__details--without-price': showWithoutPrice,
                }"
              >
                <div class="product-tile-fi__details__top">
                  <div class="product-tile-fi__price-and-delivery-details">
                    <price-savings-fi
                      class="product-tile-fi__price"
                      :best-price="currentBestPrice"
                      theme="product-list-view"
                      hide-savings-in-percent
                      stroke-price-absolute
                    />

                    <div
                      v-if="!showWithoutPrice"
                      class="product-tile-fi__vac"
                    >
                      {{ $dict.get('online-Shop.Product.Vat-excluded') }}
                    </div>

                    <div class="product-tile-fi__availability-status-container">
                      <availability-status
                        v-if="!bestPriceLoading"
                        :class="{
                          'product-tile-fi__availability-status': true,
                          'fi-mt-9': !currentBestPrice,
                        }"
                        :delivery-speed="
                          currentBestPrice ? currentBestPrice.deliverySpeed : 'NODELIVERY'
                        "
                        :stock-levels="currentBestPrice?.stockLevels || null"
                        theme="product-list-view"
                        show-status-text
                        disable-tooltip
                      />
                    </div>
                  </div>

                  <div
                    v-if="!bestPriceLoading && isSM"
                    class="product-tile-fi__add-to-cart"
                  >
                    <button-fi
                      :data-productid="product ? product.id : null"
                      :data-productname="product ? `${product.id} - ${product.name}` : null"
                      :disabled="!bestPrice || isAddingItemToCart[product.id] || outOfStock"
                      type="button"
                      class="button-fi__primary product-tile-fi__action-button product-tile-fi__action-button--onlineShop"
                      :class="!showWithoutPrice ? 'button-fi__icon' : 'button-fi--stretched'"
                      data-test="product-tile-add-to-cart"
                      :data-test-2="
                        !bestPrice || isAddingItemToCart[product.id] || outOfStock
                          ? 'product-tile-add-to-cart-disabled'
                          : null
                      "
                      @click.stop.prevent="
                        addProduct({
                          productID: product.id,
                          productRequestedQuantity: currentBestPrice.requestedQuantity,
                        })
                      "
                    >
                      <template v-if="showWithoutPrice">
                        {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
                      </template>

                      <icon-fi
                        v-else
                        class="product-tile-fi__action-button__icon"
                        icon="shopping-cart"
                        color="white"
                      />
                    </button-fi>
                  </div>
                </div>

                <div class="product-tile-fi__quantity-stepper">
                  <quantity-steps-updater
                    v-if="currentBestPrice"
                    :ref="`quantityStepper-${product.id}`"
                    :key="product.id"
                    :item="currentBestPrice"
                    :requested-quantity="0"
                    auto-width
                    @update-quantity="(quantity) => updateBestPrice(product.id, quantity)"
                    @quantity-input="onQuantityInput"
                    @value-rounded="onValueRounded"
                  />

                  <product-package-size-fi
                    class="product-tile-fi__base-price"
                    :item="currentBestPrice"
                    align-right
                  />

                  <quantity-modification-hint-fi
                    v-if="quantityIncreasedMessage"
                    class="product-tile-fi__quantity-modification-hint"
                    :hint="quantityIncreasedMessage"
                    :data-test-2="quantityDataTest2"
                    icon="info"
                    theme="product-tile-fi"
                  />
                </div>
              </div>
              <div
                v-if="!bestPriceLoading && !isSM"
                class="product-tile-fi__add-to-cart"
              >
                <button-fi
                  :data-productid="product ? product.id : null"
                  :data-productname="product ? `${product.id} - ${product.name}` : null"
                  :disabled="!bestPrice || isAddingItemToCart[product.id] || outOfStock"
                  type="button"
                  class="button-fi__primary button-fi--stretched product-tile-fi__action-button product-tile-fi__action-button--onlineShop"
                  data-test="product-tile-add-to-cart"
                  @click.stop.prevent="
                    addProduct({
                      productID: product.id,
                      productRequestedQuantity: currentBestPrice.requestedQuantity,
                    })
                  "
                >
                  {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
                </button-fi>
              </div>
            </loading-indicator-wrapper>
          </div>
        </template>

        <button-fi
          v-else-if="isRetailConnectCart"
          :data-productid="product ? product.id : null"
          :data-productname="product ? `${product.id} - ${product.name}` : null"
          :disabled="productInCart || isAddingItemToCart[product.id] || outOfStock"
          data-test="product-tile-add-to-cart"
          type="button"
          class="button-fi__primary button-fi--stretched product-tile-fi__action-button product-tile-fi__action-button--retailConnectCart"
          @click.stop.prevent="addProduct({ productID: product.id })"
        >
          {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
        </button-fi>

        <button-fi
          v-else
          :data-productid="product ? product.id : null"
          :data-productname="product ? `${product.id} - ${product.name}` : null"
          data-test="product-tile-to-cart"
          type="button"
          class="button-fi__primary button-fi--stretched product-tile-fi__action-button product-tile-fi__action-button--retailConnect"
        >
          {{ $dict.get('online-Shop.Product-Teaser.Button-Product-Text') }}
        </button-fi>
      </template>
      <template v-else>
        <div
          v-if="productPrice"
          class="product-tile-fi__uvp-price"
        >
          <div
            class="product-tile-fi__uvp-price__value"
            data-test="product-price"
          >
            {{ $dict.get('online-Shop.Product.UVP-Price-Prefix') }} {{ productPrice }}
          </div>

          <div class="product-tile-fi__uvp-price__vat">
            {{ $dict.get('online-Shop.Product.Vat-included') }}
          </div>
        </div>
      </template>
    </div>
  </component>
</template>

<script>
import {
  AvailabilityStatus,
  ButtonFi,
  CloudImageFi,
  IconFi,
  LoadingIndicatorWrapper,
  PriceSavingsFi,
  ProductPackageSizeFi,
  QuantityModificationHintFi,
} from 'atoms';
import QuantityStepsUpdater from 'components/shop-modules/quantity-steps-updater/QuantityStepsUpdater';
import breakpointMixin from 'mixins/breakpointMixin';
import priceCurrencyMixin from 'mixins/priceCurrencyMixin';
import shoppingcartMixin from 'mixins/shoppingcartMixin';
import userMixin from 'mixins/userMixin';

const importGETBESTPRICE = () => import('gql/getBestPrice.gql');

export default {
  name: 'ProductTileFi',
  components: {
    ButtonFi,
    CloudImageFi,
    IconFi,
    LoadingIndicatorWrapper,
    PriceSavingsFi,
    AvailabilityStatus,
    QuantityStepsUpdater,
    QuantityModificationHintFi,
    ProductPackageSizeFi,
  },
  mixins: [breakpointMixin, shoppingcartMixin, userMixin, priceCurrencyMixin],
  inject: ['fields'],
  props: {
    product: {
      type: Object,
      required: true,
    },
    outOfStock: {
      type: Boolean,
      default: false,
    },
    bestPrice: {
      type: Object,
      default: null,
    },
    bestPriceLoading: {
      type: Boolean,
      default: true,
    },
    isRecommendation: {
      type: Boolean,
      default: false,
    },
    isGuidedRecommandation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['product-added', 'tile-clicked'],
  data() {
    return {
      loading: false,
      getProductListById: null,
      newBestPrice: null,
      quantityIncreasedMessage: null,
      quantityDataTest2: null,
    };
  },
  computed: {
    isRetailConnect() {
      return this.$fischer.shop.isRetailConnect();
    },
    isRetailConnectCart() {
      return this.$fischer.shop.isRetailConnectCart();
    },
    isShop() {
      return (
        (this.$fischer.shop.isClosedShop() || this.$fischer.shop.isOnlineShop()) && !this.isEditMode
      );
    },
    showWithoutPrice() {
      return this.$fischer.shop.hasPurchasePermissionWithoutPrices();
    },
    productInCart() {
      let inCart = false;
      if (this.cart?.products) {
        inCart = this.cart.products.some((product) => product.articleNo === this.product.id);
      }
      return inCart;
    },
    currentBestPrice() {
      if (this.newBestPrice) {
        return this.newBestPrice;
      }

      return this.bestPrice;
    },
    productHref() {
      return this.$fischer.pages.products().url + this.product.Slug;
    },
    productPrice() {
      if (this.product.price?.priceValue && this.product?.price?.priceCurrency) {
        return this.formatPrice(this.product.price.priceValue, this.product.price.priceCurrency);
      }

      return null;
    },
  },
  created() {
    if (IS_SSR) {
      return;
    }
    if (
      this.$fischer.cartId() &&
      this.$fischer.shop.isRetailConnectCart() &&
      !this.cart?.products
    ) {
      this.getCart(false, true);
    }
  },
  methods: {
    async addProduct(data) {
      await this.addProductToCart(
        this.isRecommendation || this.isGuidedRecommandation
          ? { ...data, disableTracking: true }
          : data,
      );
      this.$emit('product-added', data);

      if (!this.isRecommendation && !this.isGuidedRecommandation && !this.isFite) {
        return;
      }

      const ref = this.$refs[`quantityStepper-${data.productID}`];
      let itemQuantity;
      if (ref) {
        itemQuantity = ref.internalRequestedQuantity
          ? ref.internalRequestedQuantity
          : ref.item.initialQuantity;
      }
      this.sendEvent(this.isRecommendation ? 'recommendation_cart' : 'guided_recommendation_cart', {
        url: window ? window.location.href : '',
        productId: data.productID,
        refererId: this.$fischer.catalogId(),
        itemQuantity,
      });
    },
    async updateBestPrice(productCode, qty) {
      try {
        const query = (await importGETBESTPRICE()).default;
        const { data, error } = await this.$apollo.query({
          query,
          errorPolicy: 'all',
          fetchPolicy: 'no-cache',
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            productCode,
            qty,
            language: this.$fischer.siteLanguage(),
            userId: this.$fischer.user.id(),
          },
        });
        if (error) {
          this.logoutIfTokenInvalid(error);
          throw new Error(error);
        }
        this.newBestPrice = data.getBestPrice;
        this.loading = false;
      } catch (error) {
        this.logoutIfTokenInvalid(error);
        console.error(error);
        this.loading = false;
      }
    },
    onQuantityInput() {
      this.loading = true;
    },
    onValueRounded(hint, dataTest2) {
      this.quantityIncreasedMessage = hint;
      this.quantityDataTest2 = dataTest2;
    },
    goToProduct(id) {
      this.$emit('tile-clicked');

      if (!this.isFite && (this.isRecommendation || this.isGuidedRecommandation)) {
        const ref = this.$refs[`quantityStepper-${id}`];
        let itemQuantity;
        if (ref) {
          itemQuantity = ref.internalRequestedQuantity
            ? ref.internalRequestedQuantity
            : ref.item.initialQuantity;
        }

        localStorage.setItem(
          'recommendationClick',
          JSON.stringify({
            refererId: this.$fischer.catalogId(),
            itemQuantity,
            guided: this.isGuidedRecommandation,
          }),
        );
      }

      window.location = this.productHref;
    },
  },
};
</script>

<style scoped lang="scss">
@import './product-tile-fi';
</style>
